import {DefinedQueryObserverResult, useIsMutating} from '@tanstack/react-query';
import React, {useState} from 'react';
import {DragDropContext} from '@hello-pangea/dnd';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {TOOLTIP_DELAY} from '../../../constants';
import Button from '../../../incase-domain/components/button/Button';
import {IPresentation, IPresentationMeta} from '../../../incase-domain/presentation';
import {IPaginatedResult, SORT} from '../../../incase-domain/search';
import {exportPresentationAction} from '../../../store/actions/presentation';
import {exportingPresentationsSelector} from '../../../store/selectors/presentation.selectors';
import {prettyDateFormat} from '../../../utils/dateTimeUtils';
import {usePresentationFavorite} from '../../hooks/usePresentationFavorite';
import {IMPORT_PRESENTATION_KEY} from '../../../service/hooks/useImportPresentation';
import {IMPORT_PPT_PRESENTATION_KEY} from '../../../service/hooks/useImportPptPresentation';
import {IMPORT_HTML_PRESENTATION_KEY} from '../../../service/hooks/useImportHtmlPresentation';
import TooltipWrapper from '../common/TooltipWrapper';
import PresentationWizard from '../presentationWizard/PresentationWizard';
import ProtectionTypeIcons from '../ProtectionTypeIcons';
import PresentationListButtons, {BUTTON_OPTIONS} from './PresentationListButtons';
import DataView, {ViewMode} from '../common/data/DataView';
import {createDataViewListStateProps, ListParams, SetListParamsOptions} from '../../../hooks/listParams';
import {viewSelector} from '../../../store/selectors/view';
import {toggleViewAction} from '../../../store/actions/view';
import FolderNav, {PRESENTATION_LIST_DROPPABLE_ID} from './folders/FolderNav';
import {FolderNameModal} from './folders/FolderNameModal';
import {FolderDeleteModal} from './folders/FolderDeleteModal';
import {MoveToFolderModal} from './folders/MoveToFolderModal';
import {usePresentationFolderView} from './folders/usePresentationFolderView';

interface PresentationListViewProps {
    presentationList: DefinedQueryObserverResult<IPaginatedResult<IPresentation>>;
    listParams: ListParams;
    setListParams: (partial: Partial<ListParams>, options?: SetListParamsOptions) => void;
    setDeletePresentations: React.Dispatch<React.SetStateAction<boolean>>;
    setPresentationToDelete: React.Dispatch<React.SetStateAction<IPresentation[]>>;
    setSendPresentationModal: React.Dispatch<React.SetStateAction<boolean>>;
    setSelectedPresentationForLinks: (presentation: IPresentation) => void;
    setSelectedForRoomCreation?: React.Dispatch<React.SetStateAction<IPresentation | undefined>>;
    setSelected: (presentations: IPresentation[]) => void;
    onCreatePresentation: () => void;
    onImportPresentation: () => void;
    onImportHtml?: () => void;
    enablePagination?: boolean;
    enableFolders?: boolean;
}

function PresentationListView(props: PresentationListViewProps): JSX.Element {
    const {
        presentationList,
        setSendPresentationModal,
        listParams,
        setListParams,
        setDeletePresentations,
        setPresentationToDelete,
        setSelectedPresentationForLinks,
        setSelectedForRoomCreation,
        setSelected,
        onCreatePresentation,
        onImportPresentation,
        onImportHtml,
        enablePagination,
        enableFolders = false,
    } = props;

    const dataViewListState = createDataViewListStateProps(listParams, setListParams);

    const {t, i18n} = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const mutation = usePresentationFavorite();
    const exportingPresentations = useSelector(exportingPresentationsSelector);
    const viewMode = useSelector(viewSelector);
    const importingCount = useIsMutating(IMPORT_PRESENTATION_KEY);
    const importingPptCount = useIsMutating(IMPORT_PPT_PRESENTATION_KEY);
    const importingHtmlCount = useIsMutating(IMPORT_HTML_PRESENTATION_KEY);
    const isImporting = importingCount > 0 || importingPptCount > 0;
    const isImportingHtml = importingHtmlCount > 0;

    const [presentationWizard, setPresentationWizard] = useState(false);
    const [selectedPresentation, setSelectedPresentation] = useState<IPresentationMeta | null>(null);

    const folderView = usePresentationFolderView(enableFolders, listParams, setListParams);

    const handleFavoriteClick = (presentation: IPresentation): void => {
        mutation.mutate(presentation);
    };

    const renderActions = (presentation: IPresentation): JSX.Element => {
        return (
            <PresentationListButtons
                presentation={presentation}
                setDeletePresentations={setDeletePresentations}
                setPresentationToDelete={setPresentationToDelete}
                onCreatePresentationLinks={setSelectedPresentationForLinks}
                setSelectedForRoomCreation={setSelectedForRoomCreation}
                setSendPresentationModal={setSendPresentationModal}
                setSelected={setSelected}
                onMovePresentation={(item) => folderView.handleMoveTargets([item])}
                buttonOptions={[
                    BUTTON_OPTIONS.CREATE_ROOM,
                    BUTTON_OPTIONS.COPY_PRESENTATION,
                    ...(enableFolders ? [BUTTON_OPTIONS.MOVE_TO_FOLDER] : []),
                    BUTTON_OPTIONS.DIVIDER,
                    BUTTON_OPTIONS.DOWNLOAD_PRESENTATION,
                    BUTTON_OPTIONS.CREATE_PRESENTATION_LINKS,
                    BUTTON_OPTIONS.SHARE_PRESENTATION,
                    BUTTON_OPTIONS.PRESENTATION_REPORTS,
                    BUTTON_OPTIONS.DIVIDER,
                    BUTTON_OPTIONS.DELETE_PRESENTATION,
                ]}
            />
        );
    };

    const renderName = (presentation: IPresentation): JSX.Element => {
        return (
            <>
                {presentation.name}
                {presentation.isShared && (
                    <TooltipWrapper id="listView-isShared" content={t('editorPage.isShared')} place="left" delayShow={TOOLTIP_DELAY}>
                        <Button
                            leading="solar:users-group-rounded-bold"
                            size="sm"
                            variant="ghost"
                            square
                            onClick={(e) => e?.stopPropagation()}
                        />
                    </TooltipWrapper>
                )}
            </>
        );
    };

    const renderLastModified = (presentation: IPresentation): JSX.Element => {
        return <>{prettyDateFormat(new Date(presentation.lastModified), i18n.language)}</>;
    };

    const renderProtection = (presentation: IPresentation): JSX.Element => {
        return <ProtectionTypeIcons protectionTypes={presentation.protectionTypes || []} />;
    };

    const renderPlay = (presentation: IPresentation): JSX.Element => {
        return (
            <Button
                className="e-table__actions_button"
                dataCy="playPresentationButton"
                size="2xs"
                leading="solar:play-bold"
                color="secondary"
                variant="solid"
                onClick={(e) => {
                    e?.stopPropagation();
                    setPresentationWizard(true);
                    setSelectedPresentation(presentation);
                }}
            >
                {t('editorPage.play')}
            </Button>
        );
    };

    const renderFavorite = (presentation: IPresentation): JSX.Element => {
        return (
            <div className="u-flex u-flex-row u-gap-1">
                <TooltipWrapper id="listView-favorite" content={t('editorPage.favorite')} place="left" delayShow={TOOLTIP_DELAY}>
                    <Button
                        variant="ghost"
                        size="sm"
                        square
                        leading={presentation.favorite ? 'solar:star-bold' : 'solar:star-linear'}
                        color={presentation.favorite ? 'warning' : 'primary'}
                        onClick={(e) => {
                            e?.stopPropagation();
                            handleFavoriteClick(presentation);
                        }}
                    />
                </TooltipWrapper>
            </div>
        );
    };

    return (
        <>
            <div data-cy="presentations-list">
                <DragDropContext onDragEnd={folderView.handleDragEnd} onDragStart={folderView.handleDragStart}>
                    {enableFolders && (
                        <FolderNav
                            folders={folderView.folders}
                            loading={folderView.foldersLoading}
                            selectedFolder={folderView.selectedFolder}
                            onSelect={folderView.handleSelectFolder}
                            onNewFolder={folderView.openCreateFolder}
                            onRenameFolder={folderView.openRenameFolder}
                            onDeleteFolder={folderView.openDeleteFolder}
                            cursorFolderKey={folderView.cursorFolderKey}
                        />
                    )}
                    <DataView
                        initialView={viewMode}
                        onViewChange={(view) => {
                            dispatch(toggleViewAction(view));
                        }}
                        views={[
                            {
                                view: ViewMode.TABLE,
                                hideHeader: false,
                                columns: [
                                    {
                                        body: (row) => renderFavorite(row),
                                        width: '3.5rem',
                                    },
                                    {
                                        label: t('editorPage.presentationTitle'),
                                        key: 'name',
                                        direction: listParams.sortBy?.name || SORT.DESC,
                                        sortable: true,
                                        body: (row) => renderName(row),
                                    },
                                    {
                                        label: t('editorPage.lastUpdate'),
                                        key: 'lastModified',
                                        direction: listParams.sortBy?.lastModified || SORT.DESC,
                                        sortable: true,
                                        body: (row) => renderLastModified(row),
                                        width: '11rem',
                                    },
                                    {
                                        label: t('editorPage.protection'),
                                        body: (row) => renderProtection(row),
                                        width: '9rem',
                                    },
                                    {
                                        body: (row) => renderPlay(row),
                                        width: '5rem',
                                    },
                                    {
                                        body: (row) => renderActions(row),
                                        width: '4rem',
                                    },
                                ],
                            },
                            {
                                view: ViewMode.GRID,
                                columnsGap: '1rem',
                                rowsGap: '1rem',
                                renderItem: (row, loading, onSelectItem, isSelected) => {
                                    return (
                                        <div className={`c-presentationItem ${isSelected ? 'c-presentationItem--selected' : ''}`}>
                                            <div className="c-presentationItem__imageWrapper" onClick={() => onSelectItem?.(row)}>
                                                {row.firstSlidePreview ? (
                                                    <img src={row.firstSlidePreview} alt={row.name} className="c-presentationItem__image" />
                                                ) : (
                                                    <div className="c-presentationItem__placeholder">
                                                        <Button
                                                            leading={loading ? 'svg-spinners:eclipse' : 'hugeicons:image-not-found-02'}
                                                            size="2xl"
                                                            variant="ghost"
                                                            disablePointerEvents
                                                            padded={false}
                                                        />
                                                        <div className="c-presentationItem__placeholder_text">
                                                            {loading ? 'Loading...' : 'No Preview Available'}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="c-presentationItem__body">
                                                <div className="c-presentationItem__meta">
                                                    <div className="u-flex u-gap-1 u-v-center">
                                                        {row.favorite && (
                                                            <Button
                                                                disablePointerEvents
                                                                size="sm"
                                                                variant="ghost"
                                                                padded={false}
                                                                leading="solar:star-bold"
                                                                color="warning"
                                                            />
                                                        )}
                                                        <div className="c-presentationItem__title">{row.name}</div>
                                                    </div>
                                                    <div className="c-presentationItem__lastUpdated">
                                                        {prettyDateFormat(new Date(row.lastModified), i18n.language)}
                                                    </div>
                                                </div>
                                                <div className="c-presentationItem__buttons">
                                                    <Button
                                                        color="frost"
                                                        variant="ghost"
                                                        size="xs"
                                                        leading="solar:play-linear"
                                                        onClick={(e) => {
                                                            e?.stopPropagation();
                                                            setPresentationWizard(true);
                                                            setSelectedPresentation(row);
                                                        }}
                                                    >
                                                        {t('editorPage.play')}
                                                    </Button>
                                                    <Button
                                                        color="frost"
                                                        variant="ghost"
                                                        size="xs"
                                                        leading="solar:pen-2-linear"
                                                        onClick={() => !row.isReadOnly && navigate(`/presentations/${row._id}`)}
                                                    >
                                                        {t('editorPage.edit')}
                                                    </Button>
                                                    <div style={{flexGrow: 1}} />
                                                    <PresentationListButtons
                                                        presentation={row}
                                                        setDeletePresentations={setDeletePresentations}
                                                        setPresentationToDelete={setPresentationToDelete}
                                                        onCreatePresentationLinks={setSelectedPresentationForLinks}
                                                        setSelectedForRoomCreation={setSelectedForRoomCreation}
                                                        onMovePresentation={(item) => folderView.handleMoveTargets([item])}
                                                        buttonOptions={[
                                                            BUTTON_OPTIONS.CREATE_ROOM,
                                                            BUTTON_OPTIONS.COPY_PRESENTATION,
                                                            ...(enableFolders ? [BUTTON_OPTIONS.MOVE_TO_FOLDER] : []),
                                                            BUTTON_OPTIONS.DIVIDER,
                                                            BUTTON_OPTIONS.DOWNLOAD_PRESENTATION,
                                                            BUTTON_OPTIONS.CREATE_PRESENTATION_LINKS,
                                                            BUTTON_OPTIONS.PRESENTATION_REPORTS,
                                                            BUTTON_OPTIONS.DIVIDER,
                                                            BUTTON_OPTIONS.DELETE_PRESENTATION,
                                                        ]}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                },
                            },
                        ]}
                        rows={presentationList.data.content}
                        rowIdentifier={(row) => row._id}
                        loading={presentationList.isFetching || presentationList.isLoading}
                        onRowClick={(row) => {
                            !row.isReadOnly && navigate(`/presentations/${row._id}`);
                        }}
                        enableSearch
                        onSelected={(items) => setSelected(items)}
                        listDroppableId={enableFolders ? PRESENTATION_LIST_DROPPABLE_ID : undefined}
                        draggableIdOf={enableFolders ? (row) => row._id : undefined}
                        selectActions={[
                            {
                                label: t('editorPage.download'),
                                onClick: (selected: IPresentation[]) => {
                                    for (let i = 0; i < selected.length; i++) {
                                        dispatch(exportPresentationAction(selected[i]._id));
                                    }
                                },
                                icon: 'solar:download-minimalistic-bold',
                                disabled: exportingPresentations.length > 0,
                                loading: !!exportingPresentations.length,
                            },
                            {
                                label: t('editorPage.share'),
                                onClick: (selected: IPresentation[]) => {
                                    setSelected(selected);
                                    setSendPresentationModal(true);
                                },
                                icon: 'solar:share-linear',
                            },
                            ...(enableFolders
                                ? [
                                      {
                                          label: t('editorPage.folders.move'),
                                          onClick: (selected: IPresentation[]) => {
                                              folderView.handleMoveTargets(selected);
                                          },
                                          icon: 'solar:folder-move-linear',
                                      },
                                  ]
                                : []),
                            {
                                label: t('editorPage.delete'),
                                onClick: () => {
                                    setDeletePresentations(true);
                                },
                                icon: 'solar:trash-bin-minimalistic-linear',
                                color: 'error',
                            },
                        ]}
                        paginationData={
                            enablePagination
                                ? {
                                      pageSize: listParams.size,
                                      currentPage: listParams.page,
                                      itemsCount: presentationList.data.numberOfElements,
                                  }
                                : undefined
                        }
                        {...dataViewListState}
                        headerActionPrimary={{
                            icon: 'ph:plus-bold',
                            title: t('genericTable.new'),
                            onClick: () => onCreatePresentation(),
                        }}
                        headerActionSecondary={{
                            icon: 'solar:upload-minimalistic-linear',
                            title: t('editorPage.import'),
                            onClick: () => onImportPresentation(),
                            loading: isImporting,
                            disabled: isImporting || isImportingHtml,
                        }}
                        headerActionTertiary={
                            onImportHtml
                                ? {
                                      icon: 'solar:code-linear',
                                      title: t('editorPage.importHtml'),
                                      onClick: () => onImportHtml(),
                                      loading: isImportingHtml,
                                      disabled: isImporting || isImportingHtml,
                                  }
                                : undefined
                        }
                    />
                </DragDropContext>
            </div>
            {enableFolders && (
                <>
                    <FolderNameModal
                        open={folderView.createFolderOpen}
                        title={t('editorPage.folders.newFolder')}
                        confirmText={t('common.confirm')}
                        saving={folderView.creatingFolder}
                        onClose={folderView.closeCreateFolder}
                        onSubmit={folderView.handleCreateFolder}
                    />
                    <FolderNameModal
                        open={!!folderView.folderToRename}
                        title={t('editorPage.folders.renameFolder')}
                        initialName={folderView.folderToRename?.name}
                        confirmText={t('common.save')}
                        saving={folderView.renamingFolder}
                        onClose={folderView.closeRenameFolder}
                        onSubmit={folderView.handleRenameFolder}
                    />
                    <FolderDeleteModal
                        folder={folderView.folderToDelete}
                        onClose={folderView.closeDeleteFolder}
                        onConfirm={folderView.handleConfirmDeleteFolder}
                    />
                    <MoveToFolderModal
                        open={folderView.moveTargets.length > 0}
                        folders={folderView.folders}
                        moving={folderView.movingPresentations}
                        onClose={folderView.closeMoveToFolder}
                        onSelect={folderView.handleMoveToFolder}
                    />
                </>
            )}
            {selectedPresentation && (
                <PresentationWizard
                    presentation={selectedPresentation}
                    open={presentationWizard}
                    close={() => {
                        setPresentationWizard(false);
                        setSelectedPresentation(null);
                    }}
                    onPresentationUpdate={setSelectedPresentation}
                />
            )}
        </>
    );
}

export default PresentationListView;
